import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import ReactJsonView from 'react-json-view';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse, faUser } from '@fortawesome/free-solid-svg-icons';
import HomeNameBadge, { HOME_BADGE_STYLE } from '../../../../../Partials/HomeNameBadge/index.jsx';
import { getBondColor } from '../../../../../../../../lib/ui/colorUtils.js';
import '../MqttEvent/MqttEvent.css';

// ── Shared style maps ─────────────────────────────────────────────────────────

// Actions arrive as e.g. CREATED, INVITE_CREATED, INVITE_CANCELLED, MEMBER_REMOVED…
// Color by verb so future HOME actions pick a sensible color without a mapping.
function actionStyle(action) {
    const a = (action || '').toUpperCase();
    if (/CANCEL|DELET|REMOV|DECLIN|REJECT|LEFT|LEAVE/.test(a)) return { background: '#dc3545', color: '#ffffff' };
    if (/CREAT|ADD|ACCEPT|JOIN/.test(a)) return { background: '#198754', color: '#ffffff' };
    if (/UPDAT|CHANG|RENAM/.test(a)) return { background: '#ca8a04', color: '#ffffff' };
    return { background: '#6f42c1', color: '#ffffff' };
}

function roleBadgeVariant(role) {
    if (role === 'owner') return 'success';
    if (role === 'dealer') return 'warning';
    return 'secondary';
}

// ── Source cell (used by LiveStream.jsx) ──────────────────────────────────────

export function HomeSourceCell({ row }) {
    const action = row.payload?.action;
    const style = actionStyle(action);

    return (
        <div className="small">
            <Badge className="w-100 mb-1 integrationBadge" style={{ '--integration-badge-bg': HOME_BADGE_STYLE.background, '--integration-badge-color': HOME_BADGE_STYLE.color, fontSize: '0.75rem' }}>
                <FontAwesomeIcon icon={faHouse} className="me-1" size="xs" />Home
            </Badge>
            {action && (
                <Badge pill className="w-100 integrationBadge" style={{ '--integration-badge-bg': style.background, '--integration-badge-color': style.color, fontSize: '0.75rem' }}>{action}</Badge>
            )}
        </div>
    );
}

// ── Payload cell ──────────────────────────────────────────────────────────────
// attrs: user_id, home_id, home_name, actor_id, actor_email, role, identifier,
// bond_id (BOND_ADDED), migrated / auto_created (flags — badge only when true),
// changes (UPDATED: { field: { from, to } })
// (invite_id is deliberately never shown, not even in the raw JSON view)

function formatChangeValue(value) {
    if (value === null || value === undefined || value === '') return '—';
    return typeof value === 'string' ? value : JSON.stringify(value);
}

/** `{ field: { from, to } }` → rows to render; tolerates a bare new value. */
function parseChanges(changes) {
    if (!changes || typeof changes !== 'object' || Array.isArray(changes)) return [];
    return Object.entries(changes).map(([field, change]) => {
        const isFromTo = change && typeof change === 'object' && !Array.isArray(change)
            && ('from' in change || 'to' in change);
        return {
            field,
            hasFrom: isFromTo && 'from' in change,
            from: isFromTo ? change.from : undefined,
            to: isFromTo ? change.to : change,
        };
    });
}

function resolveActor(homeMembers, attrs) {
    const member = Array.isArray(homeMembers)
        ? homeMembers.find(m => m.user_id === attrs.actor_id)
        : null;
    if (member?.name && attrs.actor_email) return `${member.name} · ${attrs.actor_email}`;
    if (member?.name) return member.name;
    return attrs.actor_email || attrs.actor_id || null;
}

function HomeEvent({ event, homeMembers, homes }) {
    const [showJson, setShowJson] = useState(false);

    const payload = event.payload || {};
    const attrs = payload.attributes || {};
    const { home_name, role, identifier, bond_id, migrated, auto_created } = attrs;
    const actor = resolveActor(homeMembers, attrs);
    const changes = parseChanges(attrs.changes);

    // The event's home_name is a snapshot from event time; resolve home_id
    // against the user's current homes so the badge shows the present name
    // (falling back to the snapshot when the home isn't in the list).
    const currentHome = Array.isArray(homes)
        ? homes.find(h => h.id === attrs.home_id)
        : null;
    const displayHomeName = currentHome?.name || home_name;

    const visibleAttrs = { ...attrs };
    delete visibleAttrs.invite_id;
    const sanitizedPayload = { ...payload, attributes: visibleAttrs };

    return (
        <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
            {/* Row 1: home_name · bond_id · role · identifier · flags · actor */}
            <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                <HomeNameBadge name={displayHomeName} />
                {bond_id && (
                    <Badge className="bondBadge" style={{ '--bond-badge-bg': getBondColor(bond_id), color: '#ffffff', fontSize: '0.75rem' }}>{bond_id}</Badge>
                )}
                {role && (
                    <Badge bg={roleBadgeVariant(role)} style={{ fontSize: '0.75rem' }}>{role}</Badge>
                )}
                {identifier && (
                    <Badge bg="info" text="dark" style={{ fontSize: '0.75rem' }}>{identifier}</Badge>
                )}
                {migrated === true && (
                    <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>migrated</Badge>
                )}
                {auto_created === true && (
                    <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>auto-created</Badge>
                )}
                {actor && (
                    <Badge className="integrationBadge" style={{ '--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a', fontSize: '0.75rem' }} title={attrs.actor_id || ''}>
                        <FontAwesomeIcon icon={faUser} className="me-1" size="xs" />{actor}
                    </Badge>
                )}
            </div>

            {/* Row 2 (UPDATED only): what changed, old → new */}
            {changes.length > 0 && (
                <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                    {changes.map(({ field, hasFrom, from, to }) => (
                        <Badge key={field} bg="dark" className="fw-normal" style={{ fontSize: '0.75rem' }}>
                            <span className="opacity-75 me-1">{field}</span>
                            {hasFrom && (
                                <>
                                    <span style={{ textDecoration: 'line-through', opacity: 0.65 }}>{formatChangeValue(from)}</span>
                                    <span className="mx-1">→</span>
                                </>
                            )}
                            <span className="fw-semibold">{formatChangeValue(to)}</span>
                        </Badge>
                    ))}
                </div>
            )}

            {/* Row 3: {} toggle */}
            <div className="d-flex align-items-center gap-1">
                <Badge
                    bg={showJson ? 'secondary' : 'dark'}
                    style={{ cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none', lineHeight: '1.5', padding: '0.35em 0.65em' }}
                    onClick={() => setShowJson(s => !s)}
                    title="Toggle raw JSON"
                >
                    {'{}'}
                </Badge>
            </div>

            {showJson && (
                <div style={{ marginTop: '6px', maxWidth: '100%', overflowX: 'auto' }}>
                    <ReactJsonView
                        src={sanitizedPayload}
                        className="mqttEventPayload"
                        theme="ocean"
                        borderStyle="none"
                        name={false}
                        displayObjectSize={false}
                        displayDataTypes={false}
                        collapsed={2}
                    />
                </div>
            )}
        </div>
    );
}

export default HomeEvent;
